<script setup>
// Imports
import OmniSummaryCard from 'components/UI/OmniSummaryCard.vue'
import { usePageLoading } from 'src/composables/usePageLoading'
import { overviewMetricPresentation, overviewNames } from 'src/utils/overviewStatistics'
import { getServiceIcon } from 'src/utils/servicePresentation'

// Props
const props = defineProps({
  overview: { type: String, required: true },
  label: { type: String, required: true },
  updatedAt: { type: [String, Number, Date], default: null },
})

// Stores
const authStore = useAuthStore()

// Constants
const api = useFeathers()
const { t, te, n } = useI18n({ useScope: 'global' })

// Refs
const counts = ref({})
const pending = ref(false)
const failed = ref(false)
let requestId = 0
usePageLoading(pending)

// Computed
const canRead = computed(() => overviewNames.includes(props.overview) && authStore.hasServicePermission('overview-totals', ['find_all']))
const metrics = computed(() => Object.keys(counts.value).filter((key) => !['total', 'stockProductCount'].includes(key)))

// Methods
const refreshCounts = async () => {
  const current = ++requestId
  if (!canRead.value) {
    counts.value = {}
    pending.value = false
    failed.value = false
    return
  }
  pending.value = true
  failed.value = false
  try {
    const response = await api.service('overview-totals').find({ query: { overview: props.overview } })
    if (!Number.isInteger(response?.counts?.total)) throw new Error('Overview response has no total')
    if (current === requestId) counts.value = response.counts
  } catch (error) {
    console.error('Overview totals could not be loaded', error)
    if (current === requestId) {
      counts.value = {}
      failed.value = true
    }
  } finally {
    if (current === requestId) pending.value = false
  }
}

// Watchers
watch(
  () => [props.overview, props.updatedAt, canRead.value],
  ([overview], [previousOverview] = []) => {
    if (overview !== previousOverview) counts.value = {}
    return refreshCounts()
  },
  { immediate: true },
)

// Lifecycle
onBeforeUnmount(() => {
  requestId++
})
</script>

<template>
  <template v-if="canRead">
    <div class="omni-summary-grid q-mb-md">
      <omni-summary-card
        :value="counts.total ?? null"
        :icon="getServiceIcon(overview)"
        :label="te(`Shared.OverviewCards.totals.${overview}`) ? t(`Shared.OverviewCards.totals.${overview}`) : t('Shared.OverviewCards.total', { name: label })"
        :caption="
          overview === 'stock' && counts.stockProductCount !== undefined
            ? t('Shared.OverviewCards.stockProductCount', { count: n(counts.stockProductCount) }, counts.stockProductCount)
            : ''
        "
        :hint="te(`Shared.OverviewCards.totalHints.${overview}`) ? t(`Shared.OverviewCards.totalHints.${overview}`) : t('Shared.OverviewCards.totalHint')"
        :loading="pending && counts.total === undefined"
      />
      <omni-summary-card
        v-for="key in metrics"
        :key="key"
        :value="counts[key]"
        :icon="overviewMetricPresentation[key]?.icon || 'sym_o_bar_chart'"
        :color="overviewMetricPresentation[key]?.color || 'primary'"
        :label="t(`Shared.OverviewCards.${key}`)"
        :hint="t(`Shared.OverviewCards.hints.${key}`)"
        :loading="pending && counts.total === undefined"
      />
    </div>
    <q-banner v-if="failed" rounded class="bg-orange-1 text-brown-9 q-mb-md" inline-actions>
      {{ t('Shared.OverviewCards.loadFailed') }}
      <template #action><q-btn flat :label="t('Shared.Globals.Retry')" @click="refreshCounts" /></template>
    </q-banner>
  </template>
</template>
